import NextImage, { type ImageProps as NextImageProps } from 'next/image';
import { twMerge } from 'tailwind-merge';

import { forceHttps } from 'utils/strings/slugs';

import type { FC } from 'react';

interface ImageProps extends Omit<NextImageProps, 'src' | 'alt'> {
  aspectRatio?: `${number}/${number}`;
  unsetRatio?: boolean;
  noFill?: boolean;
  url?: string;
  objectCover?: boolean;
  alt?: Maybe<string>;
  unsetMaxW?: boolean;
  borderRadius?: string;
  sizes?: string;
  maxWidth?: number;
}

export const isImageSVG = (src: ImageProps['url']): src is string => {
  if (typeof src !== 'string') {
    return false;
  }

  return src.toLowerCase().endsWith('.svg') || src.includes('image/svg+xml');
};

export const canUpdateImageFormat = (src: ImageProps['url']): boolean =>
  typeof src === 'string' && !isImageSVG(src) && (src as string).includes('res.cloudinary.com');

const getOptimizedSizes = (noFill: boolean, customSizes?: string, width?: number): string => {
  if (customSizes) {
    return customSizes;
  }

  if (noFill && width) {
    if (width <= 400) {
      return '(max-width: 640px) 90vw, (max-width: 768px) 400px, 400px';
    } else if (width <= 600) {
      return '(max-width: 640px) 90vw, (max-width: 1024px) 600px, 600px';
    } else if (width <= 900) {
      return '(max-width: 640px) 90vw, (max-width: 1024px) 50vw, 900px';
    } else {
      return `(max-width: 640px) 90vw, (max-width: 1024px) 50vw, (max-width: 1536px) 800px, ${Math.min(width, 1200)}px`;
    }
  }

  return '(max-width: 640px) 90vw, (max-width: 1024px) 50vw, (max-width: 1536px) 40vw, 800px';
};

const cloudinaryLoader = ({ src, width }: { src: string; width: number }): string => {
  // CRITICAL: Force HTTPS FIRST before any processing
  const httpsSrc = forceHttps(src) || src;

  if (!canUpdateImageFormat(httpsSrc)) {
    return httpsSrc;
  }

  const data = httpsSrc.split('/');
  const quality = 'q_auto:best';
  // No `dpr_auto` here: next/image already generates a DPR-aware srcSet (the
  // `width` we receive is the candidate the browser picks based on `sizes` and
  // its own devicePixelRatio). Cloudinary's `dpr_auto` would multiply on top of
  // that whenever the browser sends a Sec-CH-DPR client hint, double-scaling
  // every image site-wide. Leaving it out keeps delivery at exactly `w_${width}`.
  const params = ['f_auto', quality, `w_${width}`, 'c_limit', 'fl_progressive', 'fl_immutable_cache'];
  const uploadIndex = data.indexOf('upload') + 1;
  data.splice(uploadIndex, 0, params.join(','));

  return data.join('/');
};

const Image: FC<ImageProps> = ({
  url,
  alt,
  className,
  height,
  width,
  aspectRatio,
  unsetRatio,
  style,
  noFill,
  objectCover,
  unsetMaxW,
  priority,
  borderRadius,
  sizes: customSizes,
  maxWidth,
  ...restProps
}) => {
  if (!url) {
    return null;
  }

  const optimizedSizes = getOptimizedSizes(!!noFill, customSizes, typeof width === 'number' ? width : undefined);
  const shouldUseEagerLoading = width && typeof width === 'number' && width <= 100;

  const src = forceHttps(url) || url;

  // `maxWidth` caps the resolution Cloudinary actually delivers regardless of
  // which srcSet candidate next/image picks. Used to keep a small-display image
  // (e.g. a hero that never renders wider than ~512px) from being served at
  // full device-pixel-ratio on high-DPR mobile, which starves the LCP. This is
  // a sizing cap, not a quality cap: q_auto:best is untouched.
  const cappedLoader =
    maxWidth && canUpdateImageFormat(src)
      ? ({ src: loaderSrc, width: candidateWidth }: { src: string; width: number }) =>
          cloudinaryLoader({ src: loaderSrc, width: Math.min(candidateWidth, maxWidth) })
      : cloudinaryLoader;

  return (
    <picture
      className={twMerge('relative block', className)}
      style={{
        aspectRatio: unsetRatio ? undefined : aspectRatio || `${width}/${height}`,
        maxWidth: unsetMaxW ? undefined : width,
        ...style,
      }}
    >
      <NextImage
        src={src}
        alt={alt || 'Image'}
        title={alt || ''}
        fill={!noFill}
        width={noFill ? width : undefined}
        height={noFill ? height : undefined}
        className={twMerge(objectCover && 'size-full object-cover')}
        loading={priority || shouldUseEagerLoading ? 'eager' : 'lazy'}
        priority={priority}
        sizes={optimizedSizes}
        style={{
          borderRadius: borderRadius || undefined,
        }}
        loader={canUpdateImageFormat(src) ? cappedLoader : undefined}
        unoptimized={!canUpdateImageFormat(src)}
        {...(priority && { fetchPriority: 'high' as const })}
        {...restProps}
      />
    </picture>
  );
};

export default Image;
